import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import './styles/global.css'
import { useMenus } from './hooks/useMenus'
import { useFoodItems } from './hooks/useFoodItems'
import menuApiService from './services/menuApi.js'
import Login from './components/Login.jsx'
import ErrorModal from './components/ErrorModal.jsx'

function CoffeeIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <path d="M6 8h11a3 3 0 1 0 0 6H17a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8h2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M20 9v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M9 4s-.5 1 .5 2M12 4s-.5 1 .5 2M15 4s-.5 1 .5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  )
}

function SandwichIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <path d="M3.5 9.5 12 5l8.5 4.5v6L12 20l-8.5-4.5v-6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M4 12l8 4 8-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function PlateIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.8"/>
    </svg>
  )
}

function LoadingSpinner({ size = 24 }) {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      width: size,
      height: size
    }}>
      <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ 
          animation: 'spin 1s linear infinite',
          display: 'block'
        }}
      >
        <circle 
          cx="12" 
          cy="12" 
          r="10" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeDasharray="31.416" 
          strokeDashoffset="31.416"
          style={{
            animation: 'dash 1.5s ease-in-out infinite'
          }}
        />
      </svg>
    </div>
  )
}

// Section categories and courses are now loaded from API

function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : initialValue
    } catch {
      return initialValue
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {}
  }, [key, value])
  return [value, setValue]
}

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userInfo, setUserInfo] = useState(null)

  // Handle login success
  const handleLoginSuccess = (response) => {
    setIsAuthenticated(true)
    setUserInfo(response)
    // Store in sessionStorage instead of localStorage so it doesn't persist on refresh
    sessionStorage.setItem('authToken', JSON.stringify({
      manager_name: response.manager_name,
      managerid: response.managerid,
      role: response.role,
      isloggedin: response.isloggedin
    }))
  }

  // Local state for UI - no default selection
  const [selectedMenuId, setSelectedMenuId] = useState(null)
  const [selectedMenu, setSelectedMenu] = useState(null)
  const [activeSection, setActiveSection] = useLocalStorage('activeSection', 'Breakfast')
  const [activeCourseBySection, setActiveCourseBySection] = useState({})
  const [isSavingMenu, setIsSavingMenu] = useState(false)
  const [showSaveSuccess, setShowSaveSuccess] = useState(false)
  const [selectedMenuType, setSelectedMenuType] = useState('FTT') // Changed from useLocalStorage to useState
  const selectedMenuTypeRef = useRef('FTT') // Add ref to track current value
  const [showMenuRoster, setShowMenuRoster] = useState(false)
  const [showCreateSuccess, setShowCreateSuccess] = useState(false)
  const [showCreateError, setShowCreateError] = useState(false)
  const [createMessage, setCreateMessage] = useState('')
  const [showBatchAssign, setShowBatchAssign] = useState(false)
  const [batchAssignType, setBatchAssignType] = useState('FTT')
  const [batchStartDate, setBatchStartDate] = useState('')
  const [selectedMenusForBatch, setSelectedMenusForBatch] = useState([])
  const [batchAssignLoading, setBatchAssignLoading] = useState(false)
  const [assigningBatch, setAssigningBatch] = useState(false)
  const [batchAssignErrors, setBatchAssignErrors] = useState({})
  const rosterRefreshFunctionRef = useRef(null)
  const [errorMessage, setErrorMessage] = useState(null)

  // Use API hook for menus
  const { menus, selectedMenu: apiSelectedMenu, loading: menusLoading, refreshing: menusRefreshing, loadingDetails: menusLoadingDetails, switchingMenuType, error: menusError, createMenu: apiCreateMenu, updateMenu: apiUpdateMenu, updateMenuData: apiUpdateMenuData, deleteMenu: apiDeleteMenu, loadMenuDetails, refreshMenusSilently } = useMenus(selectedMenuType)
  
  // Use API hook for food items and sections
  const { foodData, loading: foodItemsLoading, refreshing: foodItemsRefreshing, error: foodItemsError } = useFoodItems(selectedMenuType)

  // Keep ref in sync with state changes
  useEffect(() => {
    selectedMenuTypeRef.current = selectedMenuType
  }, [selectedMenuType])

  // Wrapper function to update both state and ref
  const handleMenuTypeChange = useCallback((newType) => {
    // Prevent unnecessary updates if the type hasn't changed
    if (selectedMenuType === newType) return
    
    console.log('handleMenuTypeChange called with:', newType)
    console.log('Current selectedMenuType before update:', selectedMenuType)
    setSelectedMenuType(newType)
    selectedMenuTypeRef.current = newType // Update ref immediately
    console.log('selectedMenuTypeRef.current set to:', selectedMenuTypeRef.current)
  }, [selectedMenuType])

  // Reset batch assign loading when menus are loaded
  useEffect(() => {
    if (batchAssignLoading && !menusLoading && !switchingMenuType) {
      // Add a small delay to ensure the loading state is visible
      const timer = setTimeout(() => {
        setBatchAssignLoading(false)
      }, 300) // Reduced delay since we're forcing refresh
      
      return () => clearTimeout(timer)
    }
  }, [menusLoading, switchingMenuType, batchAssignLoading])

  // Update local selectedMenu when apiSelectedMenu changes
  useEffect(() => {
    setSelectedMenu(apiSelectedMenu)
  }, [apiSelectedMenu])

  // Initialize activeCourseBySection when foodData is loaded
  useEffect(() => {
    if (foodData && Object.keys(foodData).length > 0) {
      const initialCourses = {}
      Object.keys(foodData).forEach(sectionId => {
        if (foodData[sectionId].categories && foodData[sectionId].categories.length > 0) {
          initialCourses[sectionId] = foodData[sectionId].categories[0].category_id
        }
      })
      setActiveCourseBySection(initialCourses)
      
      // Ensure activeSection exists in loaded foodData
      if (!foodData[activeSection]) {
        const firstSection = Object.keys(foodData)[0]
        if (firstSection) {
          setActiveSection(firstSection)
        }
      }
    }
  }, [foodData, activeSection])

  // Update active course when section changes
  useEffect(() => {
    if (foodData && foodData[activeSection] && foodData[activeSection].categories) {
      const currentCourse = activeCourseBySection[activeSection]
      const availableCourses = foodData[activeSection].categories.map(cat => cat.category_id)
      
      // If current course is not available in the new section, set to first available course
      if (!availableCourses.includes(currentCourse)) {
        setActiveCourseBySection(prev => ({
          ...prev,
          [activeSection]: availableCourses[0]
        }))
      }
    }
  }, [activeSection, foodData, activeCourseBySection])

  // Clear selected menu when menu type changes (only if it doesn't exist in new list)
  useEffect(() => {
    if (selectedMenuId && menus.length > 0) {
      const selectedMenuExists = menus.some(menu => menu.id === selectedMenuId)
      if (!selectedMenuExists) {
        setSelectedMenuId(null)
        setSelectedMenu(null)
      }
    }
  }, [menus, selectedMenuId]) // Removed selectedMenuType from dependencies to prevent unnecessary re-runs

  // Handle menu selection - load detailed menu
  async function handleMenuSelection(menuId) {
    setSelectedMenuId(menuId)
    if (menuId) {
      try {
        await loadMenuDetails(menuId)
      } catch (error) {
        console.error('Failed to load menu details:', error)
        setErrorMessage(error.message || 'Failed to load menu details. Please try again.')
      }
    }
  }

  async function addMenu(name, description = '') {
    try {
    const newMenu = {
      name,
      description,
      menuType: selectedMenuType,
      sections: foodData && Object.keys(foodData).reduce((acc, sectionId) => {
        acc[sectionId] = foodData[sectionId].categories.reduce((courseAcc, category) => {
          courseAcc[category.category_id] = []
          return courseAcc
        }, {})
        return acc
      }, {}) || {},
    }
      const response = await apiCreateMenu(newMenu)
      
      // Show success message
      setCreateMessage(response.response_message || 'Menu created successfully!')
      setShowCreateSuccess(true)
      
      // Refresh menu list to get the updated list
      await refreshMenusSilently()
      
    } catch (error) {
      console.error('Failed to create menu:', error)
      // Show error message
      setCreateMessage(error.message || 'Failed to create menu. Please try again.')
      setShowCreateError(true)
    }
  }

  async function removeMenu(id) {
    try {
      await apiDeleteMenu(id)
    if (selectedMenuId === id) {
        setSelectedMenuId(null)
        setSelectedMenu(null)
      }
    } catch (error) {
      console.error('Failed to delete menu:', error)
      alert('Failed to delete menu. Please try again.')
    }
  }

  // Add item to menu locally (no API call)
  function addItemToMenu(sectionId, courseId, item) {
    if (!selectedMenu) return
    
    // Create a copy of the selected menu with the new item added
    const updatedMenu = {
      ...selectedMenu,
        sections: {
        ...selectedMenu.sections,
          [sectionId]: {
          ...selectedMenu.sections[sectionId],
          [courseId]: [
            ...(selectedMenu.sections[sectionId][courseId] || []),
            item
          ]
        }
      }
    }
    
    // Update the selected menu state locally
    setSelectedMenu(updatedMenu)
  }

  // Remove item from menu locally (no API call)
  function removeItemFromMenu(sectionId, courseId, itemId) {
    if (!selectedMenu) return
    
    // Create a copy of the selected menu with the item removed
    const updatedMenu = {
      ...selectedMenu,
        sections: {
        ...selectedMenu.sections,
          [sectionId]: {
          ...selectedMenu.sections[sectionId],
          [courseId]: (selectedMenu.sections[sectionId][courseId] || []).filter(item => item.food_item_id !== itemId)
        }
      }
    }
    
    // Update the selected menu state locally
    setSelectedMenu(updatedMenu)
  }

  // Save menu to API with current state
  async function saveMenu() {
    if (!selectedMenu) return
    
    try {
      setIsSavingMenu(true)
      // Use the current selectedMenuType from state (same as addMenu)
      const currentMenuType = selectedMenuType || 'FTT'
      
      // Include menuType in the menu data - use state value
      // Remove existing menuType from selectedMenu to avoid override
      const { menuType: _, ...selectedMenuWithoutType } = selectedMenu
      const menuDataWithType = {
        ...selectedMenuWithoutType,
        menuType: currentMenuType
      }
      
      // Use updateMenuData API for saving menu sections/items (third panel)
      await apiUpdateMenuData(selectedMenu.id, menuDataWithType)
      // Reload menu details to ensure selected menu has all data including sections
      if (selectedMenu.id) {
        await loadMenuDetails(selectedMenu.id)
      }
      // Refresh menu list to update counts
      await refreshMenusSilently()
      setShowSaveSuccess(true)
      // Auto-hide success popup after 3 seconds
      setTimeout(() => setShowSaveSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to save menu:', error)
      alert('Failed to save menu. Please try again.')
    } finally {
      setIsSavingMenu(false)
    }
  }

  // Batch assign menus to dates sequentially
  async function assignBatchMenus(onRefreshRoster) {
    console.log('assignBatchMenus called', { batchStartDate, selectedMenusForBatch, batchAssignType })
    console.log('onRefreshRoster type:', typeof onRefreshRoster, onRefreshRoster)
    
    // Clear previous errors
    setBatchAssignErrors({})
    
    // Validation
    const errors = {}
    
    if (!batchStartDate) {
      errors.startDate = 'Select Start Date'
    }
    
    if (selectedMenusForBatch.length === 0) {
      errors.menus = 'Please select atleast one menu'
    }
    
    // If there are validation errors, show them and return
    if (Object.keys(errors).length > 0) {
      setBatchAssignErrors(errors)
      return
    }

    try {
      setAssigningBatch(true)
      
      // Get selected menus data
      const selectedMenus = menus.filter(menu => selectedMenusForBatch.includes(menu.id))
      
      // Create assignments for each selected menu
      const assignments = selectedMenus.map((menu, index) => {
        const assignmentDate = new Date(batchStartDate)
        assignmentDate.setDate(assignmentDate.getDate() + index)
        
        return {
          menuId: menu.id,
          menuName: menu.name,
          menuType: batchAssignType,
          date: assignmentDate.toISOString().split('T')[0], // YYYY-MM-DD format
          assignedDate: assignmentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }
      })

      // Call API to save batch assignments
      const response = await menuApiService.setBatchMenuRoster(assignments)
      
      if (response.success) {
        // Refresh the menu roster to show updated assignments immediately
        if (onRefreshRoster && typeof onRefreshRoster === 'function') {
          console.log('Refreshing menu roster after successful batch assignment')
          await onRefreshRoster()
        } else {
          console.log('No refresh function available, skipping refresh')
        }
      } else {
        throw new Error(response.error || 'Failed to assign menus')
      }
      
      // Close the batch assign popup
      setShowBatchAssign(false)
      
      // Reset form
      setBatchStartDate('')
      setSelectedMenusForBatch([])
      
    } catch (error) {
      console.error('Failed to assign batch menus:', error)
      alert('Failed to assign batch menus. Please try again.')
    } finally {
      setAssigningBatch(false)
    }
  }

  // Clear validation errors when user interacts with form
  function clearStartDateError() {
    if (batchAssignErrors.startDate) {
      setBatchAssignErrors(prev => ({ ...prev, startDate: undefined }))
    }
  }

  function clearMenusError() {
    if (batchAssignErrors.menus) {
      setBatchAssignErrors(prev => ({ ...prev, menus: undefined }))
    }
  }

  async function editMenu(id, name, description) {
    try {
      await apiUpdateMenu(id, { name, description, menuType: selectedMenuType })
    } catch (error) {
      console.error('Failed to update menu:', error)
      alert('Failed to update menu. Please try again.')
    }
  }

  // Calculate real-time item counts for a menu
  function getRealTimeItemCounts(menu) {
    if (!menu || !menu.sections) {
      return { Breakfast: 0, Lunch: 0, Dinner: 0 }
    }
    
    return {
      Breakfast: Object.values(menu.sections.Breakfast || {}).flat().length,
      Lunch: Object.values(menu.sections.Lunch || {}).flat().length,
      Dinner: Object.values(menu.sections.Dinner || {}).flat().length
    }
  }

  // Get display counts for a menu (real-time for selected menu, API counts for others)
  function getDisplayCounts(menu) {
    // Only use real-time counts if we have the complete selected menu data and it's not loading
    if (menu.id === selectedMenuId && selectedMenu && selectedMenu.id === menu.id && !menusLoadingDetails) {
      return getRealTimeItemCounts(selectedMenu)
    }
    // Ensure we always return valid counts
    const apiCounts = menu.itemCounts || { Breakfast: 0, Lunch: 0, Dinner: 0 }
    return {
      Breakfast: apiCounts.Breakfast || 0,
      Lunch: apiCounts.Lunch || 0,
      Dinner: apiCounts.Dinner || 0
    }
  }

  // Refresh menus list
  async function refreshMenusList() {
    try {
      await refreshMenusSilently()
    } catch (error) {
      console.error('Failed to refresh menus:', error)
      alert('Failed to refresh menus. Please try again.')
    }
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />
  }

  // Show loading state while menus and food items are being fetched
  if (menusLoading || foodItemsLoading) {
  return (
    <>
      <div className="top-header">Menu Maker</div>
        <div className="app-shell">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: 'calc(100vh - 60px)', // Subtract header height
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              padding: '32px',
              backgroundColor: 'var(--panel)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid var(--border)'
            }}>
              <LoadingSpinner size={48} />
              <div style={{ color: 'var(--muted)', fontSize: '16px', fontWeight: '500' }}>
                Loading...
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show error state if there's an error loading menus or food items
  if (menusError || foodItemsError) {
    return (
      <>
        <div className="top-header">Menu Maker</div>
        <div className="app-shell">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: 'red' }}>
            Error loading data: {menusError || foodItemsError}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="top-header">
        <span>Menu Maker</span>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            className="roster-button"
            onClick={() => setShowMenuRoster(true)}
            aria-label="Open Menu Roster"
          >
            Menu Roster
          </button>
          <button 
            className="logout-button"
            onClick={() => {
              setIsAuthenticated(false)
              setUserInfo(null)
              sessionStorage.removeItem('authToken')
            }}
            aria-label="Logout"
            title="Logout"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14l5-5-5-5m5 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      <div className="app-shell">
      <MenuListPanel
        menus={menus}
        selectedMenuId={selectedMenuId}
        onSelectMenu={handleMenuSelection}
        onAddMenu={addMenu}
        onRemoveMenu={removeMenu}
        onEditMenu={editMenu}
        onRefresh={refreshMenusList}
        refreshing={menusRefreshing}
        switchingMenuType={switchingMenuType}
        disabled={isSavingMenu}
        getDisplayCounts={getDisplayCounts}
        selectedMenuType={selectedMenuType}
        onMenuTypeChange={handleMenuTypeChange}
      />
      <SelectedItemsPanel
        menu={selectedMenu}
        activeSection={activeSection}
        onChangeSection={setActiveSection}
        activeCourse={activeCourseBySection[activeSection]}
        onChangeCourse={(courseId) => setActiveCourseBySection({ ...activeCourseBySection, [activeSection]: courseId })}
        onAddItem={(item) => addItemToMenu(activeSection, activeCourseBySection[activeSection], item)}
        onRemoveItem={(sectionId, courseId, itemId) => removeItemFromMenu(sectionId, courseId, itemId)}
        disabled={!selectedMenu || menusLoadingDetails || isSavingMenu}
        loadingDetails={menusLoadingDetails}
        foodData={foodData}
      />
      <CatalogPanel
        section={activeSection}
        course={activeCourseBySection[activeSection]}
        menu={selectedMenu}
        foodData={foodData}
        onRemoveItem={(sectionId, courseId, itemId) => removeItemFromMenu(sectionId, courseId, itemId)}
        onSaveMenu={saveMenu}
        disabled={!selectedMenu || menusLoadingDetails || isSavingMenu}
        loadingDetails={menusLoadingDetails}
        isSavingMenu={isSavingMenu}
      />
      </div>

      {/* Save Success Popup */}
      {showSaveSuccess && (
        <div className="modal-backdrop" onClick={() => setShowSaveSuccess(false)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <div style={{ fontWeight: 600, color: '#059669' }}>Success</div>
              <button className="ghost" onClick={() => setShowSaveSuccess(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="col">
                <p style={{ margin: 0 }}>Menu saved successfully!</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="primary" onClick={() => setShowSaveSuccess(false)} style={{ width: '100%' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Success Popup */}
      {showCreateSuccess && (
        <div className="modal-backdrop" onClick={() => setShowCreateSuccess(false)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <div style={{ fontWeight: 600, color: '#059669' }}>Success</div>
              <button className="ghost" onClick={() => setShowCreateSuccess(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="col">
                <p style={{ margin: 0 }}>{createMessage}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="primary" onClick={() => setShowCreateSuccess(false)} style={{ width: '100%' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Error Popup */}
      {showCreateError && (
        <div className="modal-backdrop" onClick={() => setShowCreateError(false)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <div style={{ fontWeight: 600, color: '#dc2626' }}>Error</div>
              <button className="ghost" onClick={() => setShowCreateError(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="col">
                <p style={{ margin: 0 }}>{createMessage}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="primary" onClick={() => setShowCreateError(false)} style={{ width: '100%' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Assign Popup */}
      {showBatchAssign && (
        <div className="modal-backdrop" onClick={() => setShowBatchAssign(false)}>
          <div className="modal" style={{ maxWidth: '600px', maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ fontWeight: 600 }}>Batch Assign Menus</div>
              <button className="ghost" onClick={() => setShowBatchAssign(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="col" style={{ gap: '20px' }}>
                {/* Menu Type Tabs */}
                <div>
                  <div className="section-tabs wide">
                    <button
                      className={batchAssignType === 'FTT' ? 'tab active' : 'tab'}
                      onClick={async () => {
                        setBatchAssignType('FTT')
                        setSelectedMenusForBatch([])
                        setBatchAssignLoading(true)
                        // Update selectedMenuType to trigger API call with FTT filter
                        handleMenuTypeChange('FTT')
                      }}
                      style={{ flex: 1 }}
                    >
                      Farm To Table
                    </button>
                    <button
                      className={batchAssignType === 'PM' ? 'tab active' : 'tab'}
                      onClick={async () => {
                        setBatchAssignType('PM')
                        setSelectedMenusForBatch([])
                        setBatchAssignLoading(true)
                        // Update selectedMenuType to trigger API call with PM filter
                        handleMenuTypeChange('PM')
                      }}
                      style={{ flex: 1 }}
                    >
                      Prescribed Menu
                    </button>
                  </div>
                </div>

                {/* Start Date Picker */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Start Date:</label>
                  <input
                    type="date"
                    value={batchStartDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      setBatchStartDate(e.target.value)
                      clearStartDateError()
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: `1px solid ${batchAssignErrors.startDate ? '#ef4444' : 'var(--border)'}`,
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {batchAssignErrors.startDate && (
                    <div style={{ 
                      color: '#ef4444', 
                      fontSize: '12px', 
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {batchAssignErrors.startDate}
                    </div>
                  )}
                </div>

                {/* Menu List with Checkboxes */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Select Menus:
                    {batchAssignErrors.menus && (
                      <span style={{ 
                        color: '#ef4444', 
                        fontSize: '12px', 
                        marginLeft: '8px',
                        fontWeight: '500'
                      }}>
                        {batchAssignErrors.menus}
                      </span>
                    )}
                  </label>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    border: '1px solid var(--border)', 
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {batchAssignLoading ? (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '12px',
                        padding: '40px 20px'
                      }}>
                        <LoadingSpinner size={32} />
                        <div style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: '500' }}>
                          Loading menus...
                        </div>
                      </div>
                    ) : (
                      <>
                        {menus.filter(menu => menu.menuType === batchAssignType).map((menu, batchIndex) => (
                          <label key={menu.id || `batch-menu-${batchIndex}`} className="checkbox-label" style={{ 
                            borderBottom: '1px solid var(--border)',
                            marginBottom: '4px'
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedMenusForBatch.includes(menu.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMenusForBatch([...selectedMenusForBatch, menu.id])
                                } else {
                                  setSelectedMenusForBatch(selectedMenusForBatch.filter(id => id !== menu.id))
                                }
                                clearMenusError()
                              }}
                            />
                            <span>
                              <div style={{ fontWeight: '500' }}>{menu.name}</div>
                              {menu.description && (
                                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{menu.description}</div>
                              )}
                            </span>
                          </label>
                        ))}
                        {menus.filter(menu => menu.menuType === batchAssignType).length === 0 && (
                          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '20px' }}>
                            No {batchAssignType} menus available
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="primary" 
                onClick={() => {
                  console.log('Batch assign button clicked, rosterRefreshFunctionRef.current:', rosterRefreshFunctionRef.current)
                  assignBatchMenus(rosterRefreshFunctionRef.current)
                }}
                disabled={!batchStartDate || selectedMenusForBatch.length === 0 || assigningBatch}
                style={{ width: '100%', height: '50px' }}
              >
                {assigningBatch ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <LoadingSpinner size={16} />
                    <span>Assigning...</span>
                  </div>
                ) : (
                  'Assign Batch'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Menu Roster Full Screen */}
      {showMenuRoster && (
        <MenuRosterScreen 
          onClose={() => setShowMenuRoster(false)} 
          onOpenBatchAssign={() => setShowBatchAssign(true)}
          refreshRosterRef={rosterRefreshFunctionRef}
        />
      )}

      {/* Error Modal */}
      {errorMessage && (
        <ErrorModal 
          message={errorMessage} 
          onClose={() => setErrorMessage(null)} 
        />
      )}
    </>
  )
}

function MenuRosterScreen({ onClose, onOpenBatchAssign, refreshRosterRef }) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [menuRoster, setMenuRoster] = useState({})
  const [loadingRoster, setLoadingRoster] = useState(false)
  const [removingMenu, setRemovingMenu] = useState(null) // Track which menu is being removed
  const [showIndividualAssign, setShowIndividualAssign] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [individualAssignType, setIndividualAssignType] = useState('FTT')
  const [selectedMenuForIndividual, setSelectedMenuForIndividual] = useState('')
  const [individualAssignLoading, setIndividualAssignLoading] = useState(false)
  const [assigningIndividual, setAssigningIndividual] = useState(false)
  const [availableMenus, setAvailableMenus] = useState([])
  const [showPastDateMessage, setShowPastDateMessage] = useState(false)

  // Load menu roster for the current month
  const loadMenuRoster = async (year, month) => {
    try {
      setLoadingRoster(true)
      const response = await menuApiService.getBatchMenuRoster(year, month)
      console.log('Menu roster response:', response) // Debug log
      
      // Handle different response structures
      let rosterData = {}
      
      if (response) {
        // If response has a roaster property (external API format - note: 'roaster' spelling)
        if (response.roaster && typeof response.roaster === 'object') {
          rosterData = response.roaster
        }
        // If response has a roster property (local API format)
        else if (response.roster && typeof response.roster === 'object') {
          rosterData = response.roster
        }
        // If response itself is the roster object
        else if (typeof response === 'object' && !response.success && !response.error) {
          rosterData = response
        }
        // If response has success and roster
        else if (response.success && response.roster) {
          rosterData = response.roster
        }
        // If response has data property
        else if (response.data && typeof response.data === 'object') {
          rosterData = response.data
        }
      }
      
      setMenuRoster(rosterData)
    } catch (error) {
      console.error('Failed to load menu roster:', error)
      setMenuRoster({})
    } finally {
      setLoadingRoster(false)
    }
  }

  // Load roster when component mounts
  useEffect(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1 // JavaScript months are 0-based
    loadMenuRoster(year, month)
  }, [currentDate])

  // Refresh function for external calls
  const refreshRosterFunction = useCallback(async () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1
    await loadMenuRoster(year, month)
  }, [currentDate])

  // Set the refresh function in parent component
  useEffect(() => {
    console.log('MenuRosterScreen useEffect - refreshRosterRef:', refreshRosterRef, 'refreshRosterFunction:', refreshRosterFunction)
    if (refreshRosterRef && refreshRosterFunction) {
      refreshRosterRef.current = refreshRosterFunction
    }
  }, [refreshRosterRef, refreshRosterFunction])

  // Remove menu from specific day
  const removeMenuFromDay = async (date, menuType) => {
    const removeKey = `${date}-${menuType}` // Unique key for this removal operation
    try {
      setRemovingMenu(removeKey)
      const response = await menuApiService.removeMenuFromDay(date, menuType)
      if (response.success) {
        // Refresh the roster to show updated assignments
        await refreshRosterFunction()
      } else {
        console.error('Failed to remove menu:', response.error)
        alert('Failed to remove menu. Please try again.')
      }
    } catch (error) {
      console.error('Failed to remove menu:', error)
      alert('Failed to remove menu. Please try again.')
    } finally {
      setRemovingMenu(null)
    }
  }

  // Handle individual menu assignment
  const handleIndividualAssign = async () => {
    if (!selectedMenuForIndividual) {
      alert('Please select a menu to assign')
      return
    }

    try {
      setAssigningIndividual(true)
      
      // Get the selected menu data
      const response = await fetch('https://authalt.bsynapse.com/atmantannuggets/menulist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ menuType: individualAssignType })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const menus = await response.json()
      const selectedMenu = menus.find(menu => menu.id === selectedMenuForIndividual)
      
      if (!selectedMenu) {
        throw new Error('Selected menu not found')
      }

      // Create assignment for the selected date
      const assignment = {
        date: selectedDate,
        menuId: selectedMenu.id,
        menuName: selectedMenu.name,
        menuType: individualAssignType,
        assignedDate: new Date(selectedDate).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }

      // Call API to save individual assignment
      const assignResponse = await menuApiService.setBatchMenuRoster([assignment])
      
      if (assignResponse.success) {
        // Refresh the menu roster to show updated assignments
        await refreshRosterFunction()
        
        // Close the popup
        setShowIndividualAssign(false)
        setSelectedMenuForIndividual('')
      } else {
        throw new Error(assignResponse.error || 'Failed to assign menu')
      }
      
    } catch (error) {
      console.error('Failed to assign individual menu:', error)
      alert('Failed to assign menu. Please try again.')
    } finally {
      setAssigningIndividual(false)
    }
  }

  // Handle date click to open individual assign popup
  const handleDateClick = (dateString) => {
    // Check if the date is today or in the future
    const clickedDate = new Date(dateString)
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Reset time to start of day for accurate comparison
    
    if (clickedDate < today) {
      // Show message popup for past dates
      setShowPastDateMessage(true)
      return
    }
    
    // Proceed with normal assignment for today and future dates
    setSelectedDate(dateString)
    setShowIndividualAssign(true)
    setSelectedMenuForIndividual('')
    setIndividualAssignType('FTT')
    setIndividualAssignLoading(true)
    
    // Load menus for the default type (FTT)
    fetch('https://authalt.bsynapse.com/atmantannuggets/menulist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ menuType: 'FTT' })
    })
    .then(response => response.json())
    .then(menus => {
      setAvailableMenus(menus)
      setIndividualAssignLoading(false)
    })
    .catch(error => {
      console.error('Failed to load menus:', error)
      setAvailableMenus([])
      setIndividualAssignLoading(false)
    })
  }

  const navigateMonth = (direction) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate)
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1)
      } else {
        newDate.setMonth(newDate.getMonth() + 1)
      }
      return newDate
    })
  }

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay() // 0 = Sunday, 1 = Monday, etc.
    
    return { daysInMonth, startingDayOfWeek }
  }

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate)
  
  // Create array of days for the calendar
  const calendarDays = []
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null)
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day)
  }

  return (
    <div className="menu-roster-screen">
      <div className="roster-header">
        <h1>Menu Roster</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            className="roster-button"
            onClick={onOpenBatchAssign}
            aria-label="Batch Assign Menus"
          >
            Batch Assign
          </button>
          <button 
            className="close-button"
            onClick={onClose}
            aria-label="Close Menu Roster"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div className="roster-content">
        {/* Calendar Navigation */}
        <div className="calendar-navigation">
          <button 
            className="nav-button"
            onClick={() => navigateMonth('prev')}
            aria-label="Previous Month"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <h2 className="calendar-title">{getMonthName(currentDate)}</h2>
          
          <button 
            className="nav-button"
            onClick={() => navigateMonth('next')}
            aria-label="Next Month"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-grid" style={{ position: 'relative' }}>
          {/* Loading Overlay */}
          {loadingRoster && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '8px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                padding: '24px',
                backgroundColor: 'var(--panel)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid var(--border)'
              }}>
                <LoadingSpinner size={32} />
                <div style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: '500' }}>
                  Loading menu roster...
                </div>
              </div>
            </div>
          )}
          
          {/* Day Headers */}
          <div className="calendar-header">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="day-header">{day}</div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="calendar-days">
            {calendarDays.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="calendar-day empty-day"></div>
              }
              
              // Create date string for this day
              const year = currentDate.getFullYear()
              const month = currentDate.getMonth() + 1
              const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
              const dayAssignments = (menuRoster && menuRoster[dateString]) || {}
              
              // Check if this date is in the past
              const clickedDate = new Date(dateString)
              const today = new Date()
              today.setHours(0, 0, 0, 0) // Reset time to start of day for accurate comparison
              const isPastDate = clickedDate < today
              
              return (
                <div 
                  key={dateString || `day-${index}`} 
                  className="calendar-day has-day clickable" 
                  onClick={() => handleDateClick(dateString)}
                  title="Click to assign menu to this day"
                  style={{ cursor: 'pointer' }}
                >
                  <span className="day-number">{day}</span>
                  {dayAssignments.FTT && (
                    <div className="menu-assignment ftt" title={`FTT: ${dayAssignments.FTT.menuName}`}>
                      <span className="menu-type-indicator ftt">FTT</span>
                      <span className="menu-name">{dayAssignments.FTT.menuName}</span>
                      {!isPastDate && (
                        <button 
                          className="bin-btn" 
                          onClick={(e) => {
                            e.stopPropagation()
                            removeMenuFromDay(dateString, 'FTT')
                          }}
                          title="Remove FTT menu from this day"
                          aria-label="Remove FTT menu"
                          disabled={removingMenu === `${dateString}-FTT`}
                        >
                          {removingMenu === `${dateString}-FTT` ? (
                            <LoadingSpinner size={12} />
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  {dayAssignments.PM && (
                    <div className="menu-assignment pm" title={`PM: ${dayAssignments.PM.menuName}`}>
                      <span className="menu-type-indicator pm">PM</span>
                      <span className="menu-name">{dayAssignments.PM.menuName}</span>
                      {!isPastDate && (
                        <button 
                          className="bin-btn" 
                          onClick={(e) => {
                            e.stopPropagation()
                            removeMenuFromDay(dateString, 'PM')
                          }}
                          title="Remove PM menu from this day"
                          aria-label="Remove PM menu"
                          disabled={removingMenu === `${dateString}-PM`}
                        >
                          {removingMenu === `${dateString}-PM` ? (
                            <LoadingSpinner size={12} />
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Individual Assign Popup */}
      {showIndividualAssign && (
        <div className="modal-backdrop" onClick={() => setShowIndividualAssign(false)}>
          <div className="modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ fontWeight: 600 }}>
                Assign Menu to {selectedDate && new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <button className="ghost" onClick={() => setShowIndividualAssign(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="col" style={{ gap: '20px' }}>
                {/* Menu Type Tabs */}
                <div>
                  <div className="section-tabs wide">
                    <button
                      className={individualAssignType === 'FTT' ? 'tab active' : 'tab'}
                      onClick={async () => {
                        setIndividualAssignType('FTT')
                        setSelectedMenuForIndividual('')
                        setIndividualAssignLoading(true)
                        // Load FTT menus
                        try {
                          const response = await fetch('https://authalt.bsynapse.com/atmantannuggets/menulist', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ menuType: 'FTT' })
                          })
                          const menus = await response.json()
                          setAvailableMenus(menus)
                        } catch (error) {
                          console.error('Failed to load FTT menus:', error)
                          setAvailableMenus([])
                        } finally {
                          setIndividualAssignLoading(false)
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      Farm To Table
                    </button>
                    <button
                      className={individualAssignType === 'PM' ? 'tab active' : 'tab'}
                      onClick={async () => {
                        setIndividualAssignType('PM')
                        setSelectedMenuForIndividual('')
                        setIndividualAssignLoading(true)
                        // Load PM menus
                        try {
                          const response = await fetch('https://authalt.bsynapse.com/atmantannuggets/menulist', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ menuType: 'PM' })
                          })
                          const menus = await response.json()
                          setAvailableMenus(menus)
                        } catch (error) {
                          console.error('Failed to load PM menus:', error)
                          setAvailableMenus([])
                        } finally {
                          setIndividualAssignLoading(false)
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      Prescribed Menu
                    </button>
                  </div>
                </div>

                {/* Menu Selection */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Select Menu:
                  </label>
                  <div style={{ 
                    maxHeight: '350px', 
                    overflowY: 'auto', 
                    border: '1px solid var(--border)', 
                    borderRadius: '6px',
                    padding: '8px'
                  }}>
                    {individualAssignLoading ? (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '12px',
                        padding: '40px 20px'
                      }}>
                        <LoadingSpinner size={32} />
                        <div style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: '500' }}>
                          Loading menus...
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {availableMenus.map((menu, individualIndex) => (
                          <label key={menu.id || `individual-menu-${individualIndex}`} className="checkbox-label">
                            <input
                              type="radio"
                              name="individualMenu"
                              value={menu.id}
                              checked={selectedMenuForIndividual === menu.id}
                              onChange={() => setSelectedMenuForIndividual(menu.id)}
                            />
                            <span>
                              <div style={{ fontWeight: '500' }}>{menu.name}</div>
                              {menu.description && (
                                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{menu.description}</div>
                              )}
                            </span>
                          </label>
                        ))}
                        {availableMenus.length === 0 && (
                          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '20px' }}>
                            No {individualAssignType} menus available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="primary" 
                onClick={handleIndividualAssign}
                disabled={!selectedMenuForIndividual || assigningIndividual}
                style={{ width: '100%', height: '50px' }}
              >
                {assigningIndividual ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <LoadingSpinner size={16} />
                    <span>Assigning...</span>
                  </div>
                ) : (
                  'Assign Menu'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Past Date Message Popup */}
      {showPastDateMessage && (
        <div className="modal-backdrop" onClick={() => setShowPastDateMessage(false)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <div style={{ fontWeight: 600, color: '#dc2626' }}>Cannot Assign Menu</div>
              <button className="ghost" onClick={() => setShowPastDateMessage(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="col">
                <p style={{ margin: 0, color: 'var(--text)' }}>
                  Cannot assign menu to a previous day. Please select today or a future date.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="primary" onClick={() => setShowPastDateMessage(false)} style={{ width: '100%' }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MenuListPanel({ menus, selectedMenuId, onSelectMenu, onAddMenu, onRemoveMenu, onEditMenu, onRefresh, refreshing, switchingMenuType, disabled, getDisplayCounts, selectedMenuType, onMenuTypeChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [editingMenu, setEditingMenu] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [deletingMenu, setDeletingMenu] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Memoize the menu list to prevent unnecessary re-renders
  const memoizedMenuList = useMemo(() => {
    return menus.map((m, menuIndex) => (
      <div
        key={m.id || `menu-${menuIndex}`}
        className={`list-item clickable${m.id === selectedMenuId ? ' selected' : ''}`}
        onClick={() => onSelectMenu(m.id)}
      >
        <div>
          <div className="title">{m.name}</div>
          {m.description && <div className="meta" style={{ marginTop: 2 }}>{m.description}</div>}
          <div className="row" style={{ marginTop: 6, flexWrap: 'wrap', gap: 8 }}>
            {(() => {
              const counts = getDisplayCounts(m)
              return (
                <>
                  <span className="meta">Breakfast: {counts.Breakfast}</span>
                  <span className="meta">Lunch: {counts.Lunch}</span>
                  <span className="meta">Dinner: {counts.Dinner}</span>
                </>
              )
            })()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="ghost" onClick={(e) => { e.stopPropagation(); handleEdit(m); }} aria-label="Edit menu">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteClick(m); }} aria-label="Delete menu">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    ))
  }, [menus, selectedMenuId, getDisplayCounts, onSelectMenu])

  async function handleCreate() {
    const name = title.trim() || `Menu ${menus.length + 1}`
    
    try {
      setIsCreating(true)
      await onAddMenu(name, description.trim())
      // Close popup and refresh list on success
    setTitle('')
    setDescription('')
    setIsOpen(false)
      await onRefresh() // Refresh the menu list
    } catch (error) {
      console.error('Failed to create menu:', error)
      alert('Failed to create menu. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  function handleEdit(menu) {
    setEditingMenu(menu)
    setEditTitle(menu.name)
    setEditDescription(menu.description || '')
  }

  async function handleSaveEdit() {
    if (!editingMenu) return
    
    try {
      setIsSaving(true)
      await onEditMenu(editingMenu.id, editTitle.trim(), editDescription.trim())
      // Close popup and refresh list on success
    setEditingMenu(null)
    setEditTitle('')
    setEditDescription('')
      await onRefresh() // Refresh the menu list
    } catch (error) {
      console.error('Failed to save menu:', error)
      alert('Failed to save menu. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  function handleDeleteClick(menu) {
    setDeletingMenu(menu)
  }

  async function handleConfirmDelete() {
    if (!deletingMenu) return
    
    try {
      setIsDeleting(true)
      await onRemoveMenu(deletingMenu.id)
      // Close popup and refresh list on success
      setDeletingMenu(null)
      await onRefresh() // Refresh the menu list
    } catch (error) {
      console.error('Failed to delete menu:', error)
      alert('Failed to delete menu. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <section className={`panel ${disabled ? 'disabled' : ''}`}>
      <div className="panel-header">
        <h2>Menus</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="secondary" onClick={onRefresh} aria-label="Refresh menus">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        <button className="primary" onClick={() => setIsOpen(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 6 }}>
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          New Menu
        </button>
      </div>
                </div>
      
      {/* Menu Type Tabs */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: '#ffffff' }}>
        <div className="section-tabs wide">
          <button
            className={selectedMenuType === 'FTT' ? 'tab active' : 'tab'}
            onClick={() => onMenuTypeChange('FTT')}
            style={{ flex: 1 }}
          >
            Farm To Table
                </button>
          <button
            className={selectedMenuType === 'PM' ? 'tab active' : 'tab'}
            onClick={() => {
              console.log('PM tab clicked, current selectedMenuType:', selectedMenuType)
              onMenuTypeChange('PM')
              console.log('After onMenuTypeChange PM, selectedMenuType:', selectedMenuType)
            }}
            style={{ flex: 1 }}
          >
            Prescribed Menu
                </button>
              </div>
            </div>
      
      <div className="panel-content">
        {refreshing || switchingMenuType ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <LoadingSpinner size={32} />
            <div style={{ color: 'var(--muted)', fontSize: '14px' }}>Refreshing menus...</div>
        </div>
        ) : (
        <div className="list">
          {menus.length === 0 && <div className="empty">No menus yet. Create one.</div>}
          {memoizedMenuList}
        </div>
        )}
        {isOpen && (
          <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !isCreating) setIsOpen(false) }}>
            <div className="modal">
              <div className="modal-header">
                <div style={{ fontWeight: 600 }}>New Menu</div>
                <button className="ghost" onClick={() => setIsOpen(false)} disabled={isCreating}>✕</button>
              </div>
              <div className="modal-body">
                <div className="col">
                  <label className="muted">Title</label>
                  <input
                    placeholder="e.g., Weekend Brunch"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isCreating) handleCreate() }}
                    disabled={isCreating}
                  />
                  <label className="muted">Description</label>
                  <textarea
                    rows={3}
                    placeholder="Short summary shown in the list"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isCreating}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="secondary" onClick={() => setIsOpen(false)} disabled={isCreating}>Cancel</button>
                <button className="primary" onClick={handleCreate} disabled={isCreating}>
                  {isCreating ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <LoadingSpinner size={16} />
                      Creating...
                    </div>
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {editingMenu && (
          <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !isSaving) setEditingMenu(null) }}>
            <div className="modal">
              <div className="modal-header">
                <div style={{ fontWeight: 600 }}>Edit Menu</div>
                <button className="ghost" onClick={() => setEditingMenu(null)} disabled={isSaving}>✕</button>
              </div>
              <div className="modal-body">
                <div className="col">
                  <label className="muted">Title</label>
                  <input
                    placeholder="e.g., Weekend Brunch"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSaving) handleSaveEdit() }}
                    disabled={isSaving}
                  />
                  <label className="muted">Description</label>
                  <textarea
                    rows={3}
                    placeholder="Short summary shown in the list"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="secondary" onClick={() => setEditingMenu(null)} disabled={isSaving}>Cancel</button>
                <button className="primary" onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <LoadingSpinner size={16} />
                      Saving...
                    </div>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingMenu && (
          <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !isDeleting) setDeletingMenu(null) }}>
            <div className="modal">
              <div className="modal-header">
                <div style={{ fontWeight: 600 }}>Delete Menu</div>
                <button className="ghost" onClick={() => setDeletingMenu(null)} disabled={isDeleting}>✕</button>
              </div>
              <div className="modal-body">
                <div className="col">
                  <p>Are you sure you want to delete "{deletingMenu.name}"?</p>
                  <p style={{ color: 'var(--muted)', fontSize: '14px' }}>This action cannot be undone.</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="secondary" onClick={() => setDeletingMenu(null)} disabled={isDeleting}>Cancel</button>
                <button className="primary" onClick={handleConfirmDelete} disabled={isDeleting} style={{ backgroundColor: '#c8ae6e' }}>
                  {isDeleting ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <LoadingSpinner size={16} />
                      Deleting menu...
                    </div>
                  ) : (
                    'Yes, Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function SelectedItemsPanel({ menu, activeSection, activeCourse, onChangeSection, onChangeCourse, onAddItem, onRemoveItem, disabled, loadingDetails, foodData }) {
  const itemsInMenu = menu?.sections?.[activeSection]?.[activeCourse] ?? []
  
  // Get food items for the current section and course
  const getFoodItemsForCourse = () => {
    if (!foodData || !foodData[activeSection]) return []
    
    // If no activeCourse is set, use the first available category
    let currentCourse = activeCourse
    if (!currentCourse && foodData[activeSection].categories && foodData[activeSection].categories.length > 0) {
      currentCourse = foodData[activeSection].categories[0].category_id
    }
    
    const category = foodData[activeSection].categories?.find(cat => cat.category_id === currentCourse)
    return category ? category.food_items : []
  }
  
  const catalog = getFoodItemsForCourse()
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return catalog
    return catalog.filter(i => i.food_item_name.toLowerCase().includes(q))
  }, [catalog, query])
  return (
    <section className={`panel ${disabled ? 'disabled' : ''}`}>
      <div className="panel-header">
        <div className="section-tabs wide">
          {['Breakfast', 'Lunch', 'Dinner'].map(sectionName => (
            <button
              key={sectionName}
              className={activeSection === sectionName ? 'tab active' : 'tab'}
              onClick={() => onChangeSection(sectionName)}
            >
              {sectionName}
            </button>
          ))}
        </div>
      </div>
      <div className="panel-content">
        {loadingDetails ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '24px',
              backgroundColor: 'var(--panel)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid var(--border)'
            }}>
              <LoadingSpinner size={32} />
              <div style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: '500' }}>
                Loading menu details...
              </div>
            </div>
          </div>
        ) : (
          <>
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Categories</div>
        <div className="section-tabs" style={{ marginBottom: 8 }}>
          {foodData && foodData[activeSection]?.categories?.map((category, categoryIndex) => (
            <button
              key={category.category_id || `category-${categoryIndex}`}
              className={activeCourse === category.category_id ? 'course-tab active' : 'course-tab'}
              onClick={() => onChangeCourse(category.category_id)}
            >{category.category_name}</button>
          ))}
        </div>
        <div className="search-bar">
          <div className="search-input">
            <span className="icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </span>
            <input
                      placeholder={`Search in ${foodData && foodData[activeSection]?.categories?.find(c => c.category_id === activeCourse)?.category_name || 'Category'}`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: '100%' }}
            />
            {query && (
              <button className="clear-btn" onClick={() => setQuery('')} aria-label="Clear search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        {!menu && <div className="empty">Choose a menu to add items.</div>}
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '5px 0 10px 0' }}>
          {foodData && foodData[activeSection]?.categories?.find(c => c.category_id === activeCourse)?.category_name || 'Category'}
          </div>
        {filtered.length === 0 && <div className="empty">No items in this category.</div>}
        <div className="list">
          {filtered.map((item, itemIndex) => {
            const exists = itemsInMenu.some(x => x.food_item_id === item.food_item_id)
            return (
              <div key={item.food_item_id || `item-${itemIndex}`} className="list-item">
                <div>
                  <div className="title">{item.food_item_name}</div>
                  <div className="meta">{item.food_item_description}</div>
                </div>
                {exists ? (
                  <button className="bin-btn" onClick={() => onRemoveItem(activeSection, activeCourse, item.food_item_id)} aria-label="Remove item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                ) : (
                  <button className="primary" disabled={!menu} onClick={() => onAddItem(item)}>Add</button>
                )}
              </div>
            )
          })}
        </div>
          </>
        )}
      </div>
    </section>
  )
}

function CatalogPanel({ section, course, menu, onRemoveItem, onSaveMenu, disabled, loadingDetails, isSavingMenu, foodData }) {
  return (
    <section className={`panel ${disabled ? 'disabled' : ''}`}>
      <div className="panel-header">
        <h2>{loadingDetails ? '' : (disabled ? 'Selected Items (Select a menu)' : (menu ? menu.name : 'Selected Items'))}</h2>
      </div>
      <div className="panel-content">
        {loadingDetails ? (
          <div></div>
        ) : (
          <>
        {!menu && <div className="empty">No menu selected.</div>}
        {menu && !menu.sections && <div className="empty">Menu data is loading...</div>}
        {menu && menu.sections && (
          <div className="nested-list">
                {foodData && Object.keys(foodData).map((sectionName, sectionIndex) => {
                  const sectionItems = Object.entries((menu.sections && menu.sections[sectionName]) || {}).reduce((acc, [courseId, items]) => {
                if (items && Array.isArray(items) && items.length > 0) {
                      const courseLabel = foodData[sectionName]?.categories?.find(c => c.category_id === courseId)?.category_name || courseId
                  acc.push({ courseId, courseLabel, items })
                }
                return acc
              }, [])
              
              if (sectionItems.length === 0) return null
              
              return (
                    <div key={sectionName || `section-${sectionIndex}`} className="section-group">
                  <div className="section-header">
                        <h3>{sectionName}</h3>
                  </div>
                  <div className="courses-list">
                    {sectionItems.map(({ courseId, courseLabel, items }, courseIndex) => (
                      <div key={courseId || `course-${courseIndex}`} className="course-group">
                        <div className="course-header">{courseLabel}</div>
                        <div className="items-list">
                          {items.map((item, itemIndex) => (
                                <div key={item.food_item_id || `selected-item-${itemIndex}`} className="item-row">
                              <div>
                                    <div className="item-name">{item.food_item_name}</div>
                                    <div className="item-desc">{item.food_item_description}</div>
                              </div>
                              <button 
                                className="bin-btn" 
                                    onClick={() => onRemoveItem(sectionName, courseId, item.food_item_id)}
                                aria-label="Remove item"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
          </>
        )}
      </div>
      <div className="save-menu-area">
        <button 
          className="primary" 
          onClick={onSaveMenu}
          disabled={!menu || isSavingMenu}
          style={{ width: '100%', height: '50px' }}
        >
          {isSavingMenu ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <LoadingSpinner size={16} />
              Saving menu...
            </div>
          ) : (
            'Save Menu'
          )}
        </button>
      </div>
    </section>
  )
}

export default App
